import type { BrandThemeRecipeV2 } from '@/lib/embed/brandTheme';
import type {
  EmailSignupPreviewConfig,
  PreviewSurface,
  SponsorshipPreviewConfig,
} from '@/lib/embed/emailSignupPreview';
import type { StyleOverrideFontFace } from '@/lib/embed/styleOverrides';
import {
  parseQuizWebPreviewModel,
  type QuizWebPreviewModel,
} from '@/lib/embed/quizWebPreviewModel';
import { normalizeCtaImageAspectRatio } from '@/lib/embed/ctaImagePresentation';

export const EMBED_PREVIEW_PROTOCOL_VERSION = 2 as const;
export const EMBED_PREVIEW_READY_MESSAGE_TYPE = 'EMBED_PREVIEW_READY' as const;
export const EMBED_PREVIEW_SNAPSHOT_MESSAGE_TYPE = 'EMBED_PREVIEW_SNAPSHOT' as const;
export const EMBED_PREVIEW_APPLIED_MESSAGE_TYPE = 'EMBED_PREVIEW_APPLIED' as const;
export const EMBED_PREVIEW_FAILED_MESSAGE_TYPE = 'EMBED_PREVIEW_FAILED' as const;

export interface EmbedPreviewReady {
  type: typeof EMBED_PREVIEW_READY_MESSAGE_TYPE;
  protocolVersion: typeof EMBED_PREVIEW_PROTOCOL_VERSION;
}

export type EmbedPreviewAppearance = 'device' | 'light' | 'dark';

export interface EmbedPreviewRenderSnapshot {
  theme: BrandThemeRecipeV2 | null;
  fontFaces: StyleOverrideFontFace[];
  appearance: EmbedPreviewAppearance;
  leaderboardSignInEnabled: boolean;
  previewSurface: PreviewSurface;
  emailSignup: EmailSignupPreviewConfig | null;
  sponsorship: SponsorshipPreviewConfig | null;
}

export interface QuizWebPreviewSnapshot {
  model: QuizWebPreviewModel;
  render: EmbedPreviewRenderSnapshot;
}

export interface EmbedPreviewSnapshot {
  type: typeof EMBED_PREVIEW_SNAPSHOT_MESSAGE_TYPE;
  protocolVersion: typeof EMBED_PREVIEW_PROTOCOL_VERSION;
  revision: number;
  snapshot: QuizWebPreviewSnapshot;
}

export interface EmbedPreviewApplied {
  type: typeof EMBED_PREVIEW_APPLIED_MESSAGE_TYPE;
  protocolVersion: typeof EMBED_PREVIEW_PROTOCOL_VERSION;
  revision: number;
}

export interface EmbedPreviewFailed {
  type: typeof EMBED_PREVIEW_FAILED_MESSAGE_TYPE;
  protocolVersion: typeof EMBED_PREVIEW_PROTOCOL_VERSION;
  revision: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isNonNegativeInteger(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}

function isNullableString(value: unknown): value is string | null {
  return value === null || typeof value === 'string';
}

function isNullableCtaImageAspectRatio(value: unknown): value is number | null {
  return value === null || normalizeCtaImageAspectRatio(value) !== null;
}

function isBrandThemeRecipe(value: unknown): value is BrandThemeRecipeV2 {
  if (!isRecord(value)) return false;
  return (
    value.version === 2 &&
    (value.editorMode === 'simple' || value.editorMode === 'advanced') &&
    typeof value.primaryColor === 'string' &&
    isNullableString(value.secondaryColor) &&
    isNullableString(value.outlineColor) &&
    isNullableString(value.successColor) &&
    isNullableString(value.criticalColor) &&
    isNullableString(value.backgroundColor) &&
    ['square', 'soft', 'rounded', 'custom'].includes(String(value.cornerStyle)) &&
    (value.customRadiusPx === null ||
      (typeof value.customRadiusPx === 'number' && Number.isFinite(value.customRadiusPx))) &&
    ['atrivial', 'modern', 'editorial', 'friendly'].includes(String(value.fontPairing)) &&
    isNullableString(value.titleFontFamily) &&
    isNullableString(value.bodyFontFamily) &&
    ['neutral', 'subtle', 'bold'].includes(String(value.surfaceTint)) &&
    (value.outerCanvas === 'solid' || value.outerCanvas === 'transparent')
  );
}

function isFontFace(value: unknown): value is StyleOverrideFontFace {
  if (!isRecord(value)) return false;
  return (
    typeof value.family === 'string' &&
    typeof value.src === 'string' &&
    ['woff2', 'woff', 'truetype', 'opentype'].includes(String(value.format))
  );
}

function isEmailSignup(value: unknown): value is EmailSignupPreviewConfig {
  if (!isRecord(value)) return false;
  return (
    typeof value.enabled === 'boolean' &&
    (value.signup_type === 'button' || value.signup_type === 'embed') &&
    typeof value.title === 'string' &&
    typeof value.description === 'string' &&
    typeof value.button_cta === 'string' &&
    typeof value.confirmation_message === 'string' &&
    typeof value.signup_url === 'string' &&
    typeof value.embed_code === 'string'
  );
}

function isSponsorship(value: unknown): value is SponsorshipPreviewConfig {
  if (!isRecord(value)) return false;
  return (
    typeof value.sponsored_by_enabled === 'boolean' &&
    typeof value.sponsored_by_text === 'string' &&
    typeof value.sponsored_by_color === 'string' &&
    (value.sponsored_by_url === undefined || typeof value.sponsored_by_url === 'string') &&
    typeof value.cta_enabled === 'boolean' &&
    typeof value.cta_tagline === 'string' &&
    typeof value.cta_title === 'string' &&
    typeof value.cta_description === 'string' &&
    typeof value.cta_button_text === 'string' &&
    typeof value.cta_url === 'string' &&
    isNullableString(value.logo_storage_bucket) &&
    isNullableString(value.logo_storage_path) &&
    isNullableString(value.cta_image_storage_bucket) &&
    isNullableString(value.cta_image_storage_path) &&
    (value.cta_image_aspect_ratio === undefined ||
      isNullableCtaImageAspectRatio(value.cta_image_aspect_ratio))
  );
}

function isRenderSnapshot(value: unknown): value is EmbedPreviewRenderSnapshot {
  if (!isRecord(value)) return false;
  return (
    (value.theme === null || isBrandThemeRecipe(value.theme)) &&
    Array.isArray(value.fontFaces) &&
    value.fontFaces.every(isFontFace) &&
    (value.appearance === 'device' ||
      value.appearance === 'light' ||
      value.appearance === 'dark') &&
    typeof value.leaderboardSignInEnabled === 'boolean' &&
    (value.previewSurface === 'question' || value.previewSurface === 'end-screen') &&
    (value.emailSignup === null || isEmailSignup(value.emailSignup)) &&
    (value.sponsorship === null || isSponsorship(value.sponsorship))
  );
}

export function isEmbedPreviewReadyMessage(data: unknown): data is EmbedPreviewReady {
  return (
    isRecord(data) &&
    data.type === EMBED_PREVIEW_READY_MESSAGE_TYPE &&
    data.protocolVersion === EMBED_PREVIEW_PROTOCOL_VERSION
  );
}

export function isEmbedPreviewSnapshotMessage(data: unknown): data is EmbedPreviewSnapshot {
  return (
    isEmbedPreviewSnapshotEnvelopeMessage(data) &&
    isRecord(data.snapshot) &&
    parseQuizWebPreviewModel(data.snapshot.model) !== null &&
    isRenderSnapshot(data.snapshot.render)
  );
}

export function isEmbedPreviewSnapshotEnvelopeMessage(
  data: unknown
): data is Pick<EmbedPreviewSnapshot, 'type' | 'protocolVersion' | 'revision'> &
  Record<string, unknown> {
  return (
    isRecord(data) &&
    data.type === EMBED_PREVIEW_SNAPSHOT_MESSAGE_TYPE &&
    data.protocolVersion === EMBED_PREVIEW_PROTOCOL_VERSION &&
    isNonNegativeInteger(data.revision)
  );
}

export function isEmbedPreviewAppliedMessage(data: unknown): data is EmbedPreviewApplied {
  return (
    isRecord(data) &&
    data.type === EMBED_PREVIEW_APPLIED_MESSAGE_TYPE &&
    data.protocolVersion === EMBED_PREVIEW_PROTOCOL_VERSION &&
    isNonNegativeInteger(data.revision)
  );
}

export function isEmbedPreviewFailedMessage(data: unknown): data is EmbedPreviewFailed {
  return (
    isRecord(data) &&
    data.type === EMBED_PREVIEW_FAILED_MESSAGE_TYPE &&
    data.protocolVersion === EMBED_PREVIEW_PROTOCOL_VERSION &&
    isNonNegativeInteger(data.revision)
  );
}
